import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from 'src/services/redux/api';
import {
  CreateAttorneyRequest,
  CreateFirmAdminRequest,
  CreateWorkBetterAdminRequest,
  GetWorkBetterAdmins,
  UpdateUserRequest,
  updateWorkBetterAdminRequest,
  User,
  UserFavoriteListRequest,
  UserFavoriteListResponse,
  UserFavoriteListResult,
  UserListRequest,
  UserListResponse,
  UserListResult,
  UserNotificationsRequest,
  UserNotificationsResponse,
  UserNotificationsResult,
  UserProfileRequest,
  UserSharedListRequest,
  UserSharedListResponse,
  UserSharedListResult,
} from './types';

export const USER_FAVORITE_ITEM_TAG_TYPE = 'UserFavoriteItem' as string;
export const userFavoriteListTag = {
  type: USER_FAVORITE_ITEM_TAG_TYPE,
  id: 'UserFavoriteList',
};

export const USER_SHARED_ITEM_TAG_TYPE = 'UserSharedItem' as string;
export const userSharedListTag = {
  type: USER_SHARED_ITEM_TAG_TYPE,
  id: 'UserSharedList',
};

export const USER_TAG_TYPE = 'User' as string;
export const userListTag = { type: USER_TAG_TYPE, id: 'UserList' };
export const myProfileTag = { type: USER_TAG_TYPE, id: 'MyProfile' };

export const userApi = createApi({
  baseQuery,
  endpoints: (builder) => ({
    createAttorney: builder.mutation<void, CreateAttorneyRequest>({
      invalidatesTags: [userListTag],
      query: (body) => ({
        body,
        method: 'POST',
        url: 'user/create/attorney',
      }),
    }),
    deleteUser: builder.mutation<void, { id: number }>({
      invalidatesTags: [userListTag],
      query: ({ id }) => ({
        method: 'DELETE',
        url: `firm/user/${id}`,
      }),
    }),
    deleteUsers: builder.mutation<void, { userIds: number[] }>({
      invalidatesTags: [userListTag],
      query: (body) => ({
        body,
        method: 'DELETE',
        url: 'user/bulk',
      }),
    }),
    createFirmAdmin: builder.mutation<void, CreateFirmAdminRequest>({
      invalidatesTags: [userListTag],
      query: (body) => ({
        body,
        method: 'POST',
        url: 'user/create/firm-admin',
      }),
    }),
    createWorkBetterAdmin: builder.mutation<void, CreateWorkBetterAdminRequest>(
      {
        invalidatesTags: [userListTag],
        query: (body) => ({
          body,
          method: 'POST',
          url: 'user/workbetter/create',
        }),
      },
    ),
    getWorkBetterAdmins: builder.query<GetWorkBetterAdmins | null, void>({
      providesTags: [userListTag],
      transformResponse: (res: { data: GetWorkBetterAdmins }) =>
        res?.data || null,
      query: () => ({
        method: 'GET',
        url: 'user/workbetter/list',
      }),
    }),
    deleteWorkBetterAdmin: builder.mutation<
      { success: boolean; message: string },
      { id: number }
    >({
      invalidatesTags: [userListTag],
      query: ({ id }) => ({
        method: 'DELETE',
        url: `user/${id}`,
      }),
    }),
    updateWorkBetterAdmin: builder.mutation<void, updateWorkBetterAdminRequest>(
      {
        invalidatesTags: [myProfileTag],
        query: (body) => ({
          body,
          method: 'PUT',
          url: 'user/workbetter/update',
        }),
      },
    ),
    myProfile: builder.query<User | null, void>({
      providesTags: [myProfileTag],
      transformResponse: (res: { data: User }) => res?.data || null,
      query: () => ({
        method: 'GET',
        url: 'user/profile',
      }),
    }),
    updateUser: builder.mutation<void, UpdateUserRequest>({
      invalidatesTags: (res, err, { userId }) => [
        userListTag,
        { id: userId, type: USER_TAG_TYPE },
      ],
      query: (body) => ({
        body,
        method: 'PUT',
        url: 'firm/user',
      }),
    }),
    updateUsersBulk: builder.mutation<
      void,
      { role?: string; firmId?: number; userIds: number[] }
    >({
      invalidatesTags: [userListTag],
      query: (body) => ({
        body,
        method: 'PUT',
        url: 'user/bulk/update',
      }),
    }),
    userFavoriteList: builder.query<
      UserFavoriteListResult,
      UserFavoriteListRequest
    >({
      providesTags: [userFavoriteListTag],
      transformResponse: (res: UserFavoriteListResponse) => res.data,
      query: (params) => ({
        method: 'GET',
        params,
        url: `user/favourite/list`,
      }),
    }),
    userList: builder.query<UserListResult, UserListRequest>({
      providesTags: [userListTag],
      transformResponse: (res: UserListResponse) => res.data,
      query: (params) => ({
        method: 'GET',
        params,
        url: `firm/users`,
      }),
    }),
    userProfile: builder.query<User, UserProfileRequest>({
      providesTags: (res) => [{ id: res?.id, type: USER_TAG_TYPE }],
      transformResponse: (res: { data: User }) => res?.data,
      query: ({ id }) => ({
        method: 'GET',
        url: `user/profile/${id}`,
      }),
    }),
    userSharedList: builder.query<UserSharedListResult, UserSharedListRequest>({
      providesTags: [userSharedListTag],
      transformResponse: (res: UserSharedListResponse) => res.data,
      query: (params) => ({
        method: 'GET',
        params,
        url: `user/shared/list`,
      }),
    }),
    notifications: builder.mutation<
      UserNotificationsResult,
      UserNotificationsRequest | void
    >({
      transformResponse: (res: UserNotificationsResponse) => res.data,
      query: (params?: UserFavoriteListRequest) => ({
        method: 'GET',
        params,
        url: `user/notifications`,
      }),
    }),
    notificationsUnreadCount: builder.query<number, void>({
      transformResponse: (res: { data: number }) => res?.data || 0,
      query: () => ({
        method: 'GET',
        url: `user/notifications/unread-count`,
      }),
    }),
  }),
  reducerPath: 'userApi',
  tagTypes: [USER_TAG_TYPE, USER_FAVORITE_ITEM_TAG_TYPE],
});

export const {
  useCreateAttorneyMutation,
  useCreateFirmAdminMutation,
  useCreateWorkBetterAdminMutation,
  useDeleteUsersMutation,
  useDeleteWorkBetterAdminMutation,
  useGetWorkBetterAdminsQuery,
  useLazyMyProfileQuery,
  useLazyUserFavoriteListQuery,
  useLazyUserListQuery,
  useUserListQuery,
  useLazyUserSharedListQuery,
  useMyProfileQuery,
  useNotificationsMutation,
  useNotificationsUnreadCountQuery,
  useUpdateUserMutation,
  useUpdateUsersBulkMutation,
  useUpdateWorkBetterAdminMutation,
  useUserFavoriteListQuery,
  useUserProfileQuery,
  useUserSharedListQuery,
} = userApi;
